import { computed, defineComponent, ref } from 'vue';
import { useTagListStore } from '@/store/dictionary/tag-list.store';
import { storeToRefs } from 'pinia';
import { TagListItem, TagListParamsRequest } from '@/models/dictionary/tag-list.model';
import {
  payoutOrderListFilterFormRef,
  usePayoutOrderListFilterStore,
} from '@/store/payout-order/payout-order-filter-list.store';
import {
  DEFAULT_CONDITION_LIST,
  EXCLUDE_AND_CONDITION_LIST,
  PAYOUT_STATUS_LIST,
  PROCESSING_TYPE_LIST,
  TRANSACTION_STATUSES_LIST,
} from '@/constants/payout-order/payout-order-list-filter.constant';
import { useSegmentListStore } from '@/store/dictionary/segment-list.store';
import { useCountryListStore } from '@/store/dictionary/country-list.store';
import { usePaymentSystemListStore } from '@/store/dictionary/payment-system-list.store';
import { TransactionType } from '@/models/common/transaction-type.model';
import { LabelPosition } from '@/models/common/label-position.model';
import { ComponentSize } from '@/models/common/component-size.model';
import { usePayoutOrderListStore } from '@/store/payout-order/payout-order-list.store';
import { usePayoutOrderListFilter } from '@/hooks/payout-order/payout-order-list-filter.composables';
import { usePayoutOrderListFilterFormRules } from '@/hooks/payout-order/payout-order-list-filter-form.rules';
import { PayoutOrderListFilterProperties } from '@/models/payout-order/payout-order-list-filter.model';
import { debounce } from 'lodash';
import HintComponent from '@/components/hint/hint';
import { PAYOUT_ORDER_HINT_MAP } from '@/constants/payout-order/payout-order-hint.constant';
import { PayoutOrderHintNames } from '@/models/payout-order/payout-order-hint-names.model';
import { SegmentListItem } from '@/models/dictionary/segment-list.model';
import { tagListToTagIdList } from '@/mappers/dictionary/tag-list-to-tag-id-list.mapper';
import { segmentListToSegmentIdList } from '@/mappers/dictionary/segment-list-to-segment-id-list.mapper';
import { ConditionListValueProperty } from '@/models/dictionary/condition-list.model';

export default defineComponent({
  name: 'PayoutOrderListFilter',
  components: { HintComponent },
  setup() {
    const MIN_SEARCH_LENGTH = 2;
    const searchTagIdListText = ref<string>('');
    const searchSegmentIdListText = ref<string>('');

    const { whiteLabelIdList } = usePayoutOrderListFilter();
    const { payoutOrderListFilterFormRules, clearConditionValidation } = usePayoutOrderListFilterFormRules();

    const countryListStore = useCountryListStore();
    const { countryList, isCountryListLoading } = storeToRefs(countryListStore);
    const { getCountryList } = countryListStore;

    const tagListStore = useTagListStore();
    const { isTagListLoading, tagList } = storeToRefs(tagListStore);
    const { getTagList, setTagList } = tagListStore;

    const segmentListStore = useSegmentListStore();
    const { segmentList, isSegmentListLoading } = storeToRefs(segmentListStore);
    const { getSegmentList, setSegmentList } = segmentListStore;

    const paymentSystemListStore = usePaymentSystemListStore();
    const {
      paymentSystemList,
      isPaymentSystemListLoading,
      isPaymentSystemListNotEmpty,
    } = storeToRefs(paymentSystemListStore);
    const { getPaymentSystemList } = paymentSystemListStore;

    const payoutOrderListFilterStore = usePayoutOrderListFilterStore();
    const {
      payoutOrderListFilterFormData,
      isFilterTagIdListNotEmpty,
      isFilterSegmentIdListNotEmpty,
      isFilterCountryListNotEmpty,
      isFilterPaymentSystemListNotEmpty,
    } = storeToRefs(payoutOrderListFilterStore);
    const {
      setTagIdListCondition,
      setSegmentIdListCondition,
      setPaymentSystemListCondition,
      setCountryListCondition,
    } = payoutOrderListFilterStore;

    const { isPayoutOrderListLoading } = storeToRefs(usePayoutOrderListStore());

    const tagOptionList = computed<TagListItem[]>(() => {
      const selectedTagList: TagListItem[] = payoutOrderListFilterFormData.value.tagList ?? [];
      const selectedTagIdSet = new Set<string>(tagListToTagIdList(selectedTagList));

      return [
        ...selectedTagList,
        ...tagList.value.filter(({ tagId }: TagListItem) => !selectedTagIdSet.has(tagId)),
      ];
    });

    const segmentOptionList = computed<SegmentListItem[]>(() => {
      const selectedSegmentList: SegmentListItem[] = payoutOrderListFilterFormData.value.segmentList ?? [];
      const selectedSegmentIdSet = new Set<string>(segmentListToSegmentIdList(selectedSegmentList));

      return [
        ...selectedSegmentList,
        ...segmentList.value.filter(({ id }: SegmentListItem) => !selectedSegmentIdSet.has(id)),
      ];
    });

    const noDataTagIdListText = computed<string>(() => {
      if (searchTagIdListText.value.length > 0 && searchTagIdListText.value.length < MIN_SEARCH_LENGTH) {
        return `Enter at least ${MIN_SEARCH_LENGTH} characters`;
      }

      return 'No data';
    });

    const noDataSegmentIdListText = computed<string>(() => {
      if (searchSegmentIdListText.value.length > 0 && searchSegmentIdListText.value.length < MIN_SEARCH_LENGTH) {
        return `Enter at least ${MIN_SEARCH_LENGTH} characters`;
      }

      return 'No data';
    });

    const loadTagIdList = debounce(async (query: string): Promise<void> => {
      if (query.length < MIN_SEARCH_LENGTH) {
        setTagList([]);

        return;
      }

      await getTagList({
        object: TagListParamsRequest.PLAYER,
        filterList: { wlIdList: whiteLabelIdList.value, name: query },
        isForceUpdate: true,
      });
    }, 300);

    const remoteTagIdListMethod = (query: string): void => {
      const value: string = query.trim();
      searchTagIdListText.value = value;
      loadTagIdList(value);
    };

    const changeTagIdListVisible = (visible: boolean): void => {
      if (!visible) {
        searchTagIdListText.value = '';
        setTagList([]);
      }
    };

    const loadSegmentIdList = debounce(async (query: string): Promise<void> => {
      if (query.length < MIN_SEARCH_LENGTH) {
        setSegmentList([]);

        return;
      }

      getSegmentList({
        filterList: { wlIdList: whiteLabelIdList.value, isPublished: true, name: query },
        isForceUpdate: true,
      });
    }, 300);

    const remoteSegmentsIdListMethod = (query: string): void => {
      const value: string = query.trim();
      searchSegmentIdListText.value = value;
      loadSegmentIdList(value);
    };

    const changeSegmentIdListVisible = (visible: boolean): void => {
      if (!visible) {
        searchSegmentIdListText.value = '';
        setSegmentList([]);
      }
    };

    const loadPaymentSystemOnVisibleChange = (visible: boolean): void => {
      if (!visible || isPaymentSystemListNotEmpty.value) {
        return;
      }

      getPaymentSystemList({
        filterList: { wlIdList: whiteLabelIdList.value, paymentType: [TransactionType.WITHDRAW] },
      });
    };

    const loadCountryListOnVisibleChange = (visible: boolean): void => {
      if (!visible || countryList.value.length) {
        return;
      }

      getCountryList();
    };

    const changeTagIdList = (value: TagListItem[]): void => {
      if (!value.length) {
        clearConditionValidation(PayoutOrderListFilterProperties.TAG_ID_LIST_CONDITION);
        setTagIdListCondition('');
        setTagList([]);

        return;
      }

      if (!payoutOrderListFilterFormData.value.tagIdListCondition) {
        setTagIdListCondition(ConditionListValueProperty.OR);
      }
    };

    const changeSegmentList = (value: SegmentListItem[]): void => {
      if (!value.length) {
        clearConditionValidation(PayoutOrderListFilterProperties.SEGMENT_ID_LIST_CONDITION);
        setSegmentIdListCondition('');

        return;
      }

      if (!payoutOrderListFilterFormData.value.segmentIdListCondition) {
        setSegmentIdListCondition(ConditionListValueProperty.OR);
      }
    };

    const changePaymentSystemList = (value: string[]): void => {
      if (!value.length) {
        clearConditionValidation(PayoutOrderListFilterProperties.PAYMENT_SYSTEM_LIST_CONDITION);
        setPaymentSystemListCondition('');

        return;
      }

      if (!payoutOrderListFilterFormData.value.paymentSystemListCondition) {
        setPaymentSystemListCondition(ConditionListValueProperty.OR);
      }
    };

    const changeCountryList = (value: string[]): void => {
      if (!value.length) {
        clearConditionValidation(PayoutOrderListFilterProperties.COUNTRY_LIST_CONDITION);
        setCountryListCondition('');

        return;
      }

      if (!payoutOrderListFilterFormData.value.countryListCondition) {
        setCountryListCondition(ConditionListValueProperty.OR);
      }
    };

    return {
      isFilterTagIdListNotEmpty,
      isFilterSegmentIdListNotEmpty,
      isFilterCountryListNotEmpty,
      isFilterPaymentSystemListNotEmpty,
      isTagListLoading,
      tagOptionList,
      payoutOrderListFilterFormData,
      isSegmentListLoading,
      DEFAULT_CONDITION_LIST,
      TRANSACTION_STATUSES_LIST,
      PROCESSING_TYPE_LIST,
      PAYOUT_STATUS_LIST,
      EXCLUDE_AND_CONDITION_LIST,
      countryList,
      isCountryListLoading,
      paymentSystemList,
      isPaymentSystemListLoading,
      LabelPosition,
      ComponentSize,
      loadPaymentSystemOnVisibleChange,
      loadCountryListOnVisibleChange,
      isPayoutOrderListLoading,
      payoutOrderListFilterFormRules,
      payoutOrderListFilterFormRef,
      PayoutOrderListFilterProperties,
      changeTagIdList,
      changeSegmentList,
      changePaymentSystemList,
      changeCountryList,
      PAYOUT_ORDER_HINT_MAP,
      PayoutOrderHintNames,
      noDataTagIdListText,
      remoteTagIdListMethod,
      changeTagIdListVisible,
      noDataSegmentIdListText,
      remoteSegmentsIdListMethod,
      changeSegmentIdListVisible,
      segmentOptionList,
    };
  },
});
